import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  PRODUCT_STATUS_META,
  STUDIO_TABS,
  buildStorefrontProductUrl,
  fill,
  formatDateTime,
  getCoverImage,
  getProductStatusKey,
  isFilledMedia,
  summarizeStock,
} from "./studioEditModel";
import { Btn, ConfirmDialog, Icon, Menu, Pill, Thumb, cx } from "./StudioUi";

const TAB_META = {
  overview: { icon: "overview", labelKey: "tabOverview" },
  media: { icon: "image", labelKey: "tabMedia" },
  variants: { icon: "variants", labelKey: "tabVariants" },
  inventory: { icon: "cube", labelKey: "tabStock" },
  details: { icon: "details", labelKey: "tabDetails" },
  publishing: { icon: "globe", labelKey: "tabPublishing" },
  history: { icon: "history", labelKey: "tabHistory" },
};

const StudioShell = ({
  studio,
  activeTab,
  onSelectTab,
  dirtySections,
  structureDirty,
  isSavingAll,
  onSaveAll,
  onDiscardAll,
  onRefresh,
  children,
}) => {
  const { tt, workspace, mediaDrafts, liveVariants, inventoryMode, brands, merchandisingTypes } = studio;
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const tabScrollerRef = useRef(null);

  // Keep the selected tab visible in the horizontally scrolling tab bar
  // (narrow screens). Only the tab bar scrolls — never the page.
  useEffect(() => {
    const scroller = tabScrollerRef.current;
    const tab = scroller?.querySelector(`#pse-tab-${activeTab}`);
    if (!scroller || !tab) return;
    const left = tab.offsetLeft - scroller.offsetLeft;
    const right = left + tab.offsetWidth;
    if (left < scroller.scrollLeft) {
      scroller.scrollLeft = Math.max(0, left - 16);
    } else if (right > scroller.scrollLeft + scroller.clientWidth) {
      scroller.scrollLeft = right - scroller.clientWidth + 16;
    }
  }, [activeTab]);
  const product = workspace.product;
  const statusKey = getProductStatusKey(workspace);
  const statusMeta = PRODUCT_STATUS_META[statusKey];
  const cover = getCoverImage(mediaDrafts);
  const storefrontUrl = statusKey === "published" ? buildStorefrontProductUrl(product.slug) : "";
  const stock = summarizeStock(liveVariants, inventoryMode);
  const familyLabel =
    studio.productFamilyOptions.find((option) => option.value === product.productFamily)?.label ||
    product.productFamily;
  const brandName =
    product.brandName || brands.find((brand) => Number(brand.id) === Number(product.brandId))?.name || "";
  const typeName =
    product.merchandisingTypeName ||
    merchandisingTypes.find((type) => Number(type.id) === Number(product.merchandisingTypeId))?.name ||
    "";
  const hasDirty = dirtySections.length > 0;

  const tabBadges = {
    media: String(mediaDrafts.filter(isFilledMedia).length),
    variants: String(liveVariants.length),
    inventory: stock.out + stock.low > 0 ? "!" : null,
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(String(product.id));
      studio.showSuccess(tt("idCopied"));
    } catch (_error) {
      // Clipboard can be blocked; the ID is visible on screen anyway.
    }
  };

  const handleTabKeyDown = (event, index) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const nextIndex = (index + keys[event.key] + STUDIO_TABS.length) % STUDIO_TABS.length;
    onSelectTab(STUDIO_TABS[nextIndex]);
    document.getElementById(`pse-tab-${STUDIO_TABS[nextIndex]}`)?.focus();
  };

  return (
    <>
      <div className="pse-topline">
        <Link to="/admin/catalog/products" className="pse-back">
          <Icon name="arrowLeft" size={16} />
          <span>{tt("back")}</span>
        </Link>
      </div>

      <header className="pse-header">
        <div className="pse-header__identity">
          <Thumb src={cover?.sourceUrl} alt="" size={76} className="pse-header__thumb" fallbackLabel={tt("noImage")} />
          <div className="pse-header__text">
            <div className="pse-header__title-row">
              <h1 className="pse-header__title">{product.title || "—"}</h1>
              <Pill tone={statusMeta.tone}>{tt(statusMeta.labelKey)}</Pill>
            </div>
            <div className="pse-header__meta">
              {[familyLabel, brandName, typeName].filter(Boolean).map((part, index) => (
                <span key={`${part}-${index}`} className="pse-header__crumb">
                  {part}
                </span>
              ))}
              <button type="button" className="pse-header__id" onClick={copyId} title={tt("copyId")}>
                {tt("idLabel")}: {product.id}
                <Icon name="copy" size={13} />
              </button>
              <span className="pse-header__date">
                {tt("created")}: {formatDateTime(product.createdAt)}
              </span>
              <span className="pse-header__date">
                {tt("updated")}: {formatDateTime(product.updatedAt)}
              </span>
            </div>
          </div>
        </div>

        <div className="pse-header__actions">
          {storefrontUrl ? (
            <a className="pse-btn pse-btn--secondary pse-btn--md" href={storefrontUrl} target="_blank" rel="noopener noreferrer">
              <span className="pse-btn__label">{tt("viewInStore")}</span>
              <Icon name="external" size={16} />
            </a>
          ) : (
            <Btn variant="secondary" iconRight="external" disabled title={tt("viewInStoreDisabled")}>
              {tt("viewInStore")}
            </Btn>
          )}
          <Menu
            variant="button"
            buttonLabel={tt("moreActions")}
            label={tt("moreActions")}
            items={[
              liveVariants.length
                ? { label: tt("moreAddStock"), icon: "plus", onClick: () => studio.openRestock() }
                : null,
              liveVariants.length && inventoryMode === "aggregate"
                ? { label: tt("moreAdjustStock"), icon: "sliders", onClick: () => studio.openAdjust() }
                : null,
              liveVariants.some((variant) => variant.barcode)
                ? {
                    label: tt("morePrintLabels"),
                    icon: "printer",
                    onClick: () => onSelectTab("variants"),
                  }
                : null,
              { label: tt("copyId"), icon: "copy", onClick: copyId },
              { label: tt("moreRefresh"), icon: "refresh", onClick: onRefresh },
            ]}
          />
          <Btn
            variant={hasDirty ? "primary" : "secondary"}
            icon={hasDirty ? null : "check"}
            onClick={onSaveAll}
            disabled={!hasDirty || isSavingAll}
            className="pse-header__save"
          >
            {isSavingAll ? tt("saving") : hasDirty ? tt("saveChanges") : tt("allSaved")}
          </Btn>
        </div>
      </header>

      <nav className="pse-tabs" id="pse-tabs" aria-label={tt("tabsAria")}>
        <div className="pse-tabs__scroller" role="tablist" ref={tabScrollerRef}>
          {STUDIO_TABS.map((tab, index) => {
            const meta = TAB_META[tab];
            const selected = tab === activeTab;
            const badge = tabBadges[tab];
            return (
              <button
                key={tab}
                id={`pse-tab-${tab}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={`pse-panel-${tab}`}
                tabIndex={selected ? 0 : -1}
                className={cx("pse-tab", selected && "is-active")}
                onClick={() => onSelectTab(tab)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                <Icon name={meta.icon} size={17} />
                <span>{tt(meta.labelKey)}</span>
                {badge ? (
                  <span className={cx("pse-tab__badge", badge === "!" && "pse-tab__badge--warn")}>
                    {badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </nav>

      {children}

      {hasDirty ? (
        <div className="pse-savebar" role="region" aria-live="polite" aria-label={tt("unsavedTitle")}>
          <div className="pse-savebar__text">
            <Icon name="alert" size={18} />
            <div>
              <strong>{tt("unsavedTitle")}</strong>
              <span>{fill(tt("unsavedIn"), { sections: dirtySections.join(", ") })}</span>
              {structureDirty ? <span className="pse-savebar__note">{tt("structurePendingHint")}</span> : null}
            </div>
          </div>
          <div className="pse-savebar__actions">
            <Btn variant="ghost" onClick={() => setIsDiscardOpen(true)} disabled={isSavingAll}>
              {tt("discardChanges")}
            </Btn>
            <Btn variant="primary" icon="check" onClick={onSaveAll} disabled={isSavingAll}>
              {isSavingAll ? tt("saving") : tt("saveChanges")}
            </Btn>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={isDiscardOpen}
        title={tt("discardConfirmTitle")}
        confirmLabel={tt("discardConfirmAction")}
        cancelLabel={tt("cancel")}
        tone="danger"
        onCancel={() => setIsDiscardOpen(false)}
        onConfirm={() => {
          setIsDiscardOpen(false);
          onDiscardAll();
        }}
      >
        {tt("discardConfirmCopy")}
      </ConfirmDialog>
    </>
  );
};

export default StudioShell;
